import { useEffect, useState } from "react";

import { Elevation, Tag } from "@blueprintjs/core";

import actionItem from "@/../../hypetrain/static/img/benefit-icons/action-items.svg";
import aiRoleplay from "@/../../hypetrain/static/img/benefit-icons/ai-roleplay.svg";
import announcement from "@/../../hypetrain/static/img/benefit-icons/announcement.svg";
import article from "@/../../hypetrain/static/img/benefit-icons/article.svg";
import groupMeeting from "@/../../hypetrain/static/img/benefit-icons/group-meeting.svg";
import seminars from "@/../../hypetrain/static/img/benefit-icons/seminars.svg";
import survey from "@/../../hypetrain/static/img/benefit-icons/survey.svg";
import video from "@/../../hypetrain/static/img/benefit-icons/video.svg";
import workshops from "@/../../hypetrain/static/img/benefit-icons/workshops.svg";
import coaching from "@/../../hypetrain/static/img/benefit-icons/yellow-coaching.svg";
import lightbulb from "@/../../hypetrain/static/img/benefit-icons/yellow-lightbulb.svg";
import sun from "@/../../hypetrain/static/img/benefit-icons/yellow-sun.svg";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Box, Flex } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { Checkbox } from "@/components/pieces/form/Checkbox";
import { OptionCard } from "@/components/pieces/onboarding/OptionCard";
import { BRAND_PURPLE, SUBTEXT_COLOR } from "@/css/constants";
import { ComponentTypeEnum } from "@/graphql";
import { ConfigureInput, NextInputProps } from "@/types/programs";

export interface ProgramComponentCreationType {
  id: number;
  name: string;
  icon: string;
  value: ComponentTypeEnum;
  description: string;
  active: boolean;
  group: string;
}

export const programComponentTypes: ProgramComponentCreationType[] = [
  {
    id: 1,
    name: "Announcement",
    icon: announcement,
    value: ComponentTypeEnum.Announcement,
    description: "Share important information or instructions",
    active: true,
    group: "General",
  },
  {
    id: 2,
    name: "Participant Survey",
    icon: survey,
    value: ComponentTypeEnum.Survey,
    description: "Capture participant perspectives",
    active: true,
    group: "Insights",
  },
  {
    id: 3,
    name: "Stakeholder Insights",
    icon: sun,
    value: ComponentTypeEnum.StakeholderInsight,
    description: "Survey stakeholders about participants",
    active: false,
    group: "Insights",
  },
  {
    id: 4,
    name: "Assessment",
    icon: lightbulb,
    value: ComponentTypeEnum.SoloActivity,
    description: "Explore personality and behavior",
    active: false,
    group: "Insights",
  },
  {
    id: 5,
    name: "AI Roleplays",
    icon: aiRoleplay,
    value: ComponentTypeEnum.AiRoleplay,
    description: "Train with AI-powered simulations.",
    active: true,
    group: "Individual Development",
  },
  {
    id: 6,
    name: "Article",
    icon: article,
    value: ComponentTypeEnum.Article,
    description: "Have your participants read a piece of content",
    active: true,
    group: "Individual Development",
  },
  {
    id: 7,
    name: "Video",
    icon: video,
    value: ComponentTypeEnum.Video,
    description: "Have your participants watch a piece of content",
    active: true,
    group: "Individual Development",
  },
  {
    id: 8,
    name: "Action Items",
    icon: actionItem,
    value: ComponentTypeEnum.ActionItem,
    description: "Engage participants with tasks, assignments, etc.",
    active: true,
    group: "Individual Development",
  },
  {
    id: 9,
    name: "1:1 Coaching",
    icon: coaching,
    value: ComponentTypeEnum.OneOneCoachingMaster,
    description: "Have participants meet with expert coaches",
    active: true,
    group: "Individual Development",
  },
  {
    id: 10,
    name: "AI Coaching",
    icon: coaching,
    value: ComponentTypeEnum.AiReinforcementCoaching,
    description: "Provide personalized guidance",
    active: false,
    group: "Individual Development",
  },
  {
    id: 11,
    name: "Workshops",
    icon: workshops,
    value: ComponentTypeEnum.Workshops,
    description: "Collaborate in interactive sessions",
    active: false,
    group: "Collaborative Learning",
  },
  {
    id: 12,
    name: "Group Meeting",
    icon: groupMeeting,
    value: ComponentTypeEnum.GroupMeeting,
    description: "Foster group learning and support",
    active: true,
    group: "Collaborative Learning",
  },
  {
    id: 13,
    name: "Seminars",
    icon: seminars,
    value: ComponentTypeEnum.Seminars,
    description: "Acquire insights from expert talks",
    active: false,
    group: "Collaborative Learning",
  },
];

// Add component types that are enabled in the test workspace
export const ENABLED_WORKSPACE_TEST_COMPONENTS: ComponentTypeEnum[] = [];

export const getActiveComponentTypes = (workspaceSlug?: string) =>
  programComponentTypes
    .filter(
      (programComponentType) =>
        programComponentType.active ||
        (workspaceSlug === "test" &&
          ENABLED_WORKSPACE_TEST_COMPONENTS.includes(
            programComponentType.value,
          )),
    )
    .map((option) => ({ ...option, active: true }));

interface OptionProps {
  option: ProgramComponentCreationType;
  onClick?: () => void;
  elevation: Elevation;
  active?: boolean;
  hasChecked?: boolean;
  selected?: boolean;
}

export function Option({
  option,
  onClick = () => {},
  elevation = 2,
  active = true,
  hasChecked = false,
  selected = false,
}: OptionProps) {
  const { icon, description, name } = option || {};
  return (
    <OptionCard
      interactive={active}
      onClick={() => onClick()}
      elevation={elevation}
      p={0}
      className={active ? "" : "inactive-grey-icon"}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        pl={3}
        pr={3}
        height={90}
        style={
          !active ? { opacity: 0.5, cursor: "default" } : { cursor: "pointer" }
        }
      >
        <Flex
          alignItems="center"
          width={1}
          style={{
            transition: "color 200ms ease",
          }}
        >
          {icon && (
            <Box mr={3}>
              <LazyImg width={60} src={icon} />
            </Box>
          )}
          <Box flex={1}>
            <Box>
              <Box fontWeight={600} mr={1} display={"inline-block"}>
                {name}
              </Box>
            </Box>
            {description ? (
              <Box color={SUBTEXT_COLOR} mt={1}>
                {description}
              </Box>
            ) : null}
          </Box>
          {!active && (
            <Box ml={3}>
              <Tag
                minimal
                round
                style={{
                  background: "#DDD9EE",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                COMING SOON
              </Tag>
            </Box>
          )}
        </Flex>
        {hasChecked && (
          <Checkbox
            hasBorders={false}
            onChange={() => null}
            checked={selected}
            checkedColor={BRAND_PURPLE}
          />
        )}
      </Flex>
    </OptionCard>
  );
}

interface ChooseTypeProps {
  onNext: (data: NextInputProps) => void;
  input: ConfigureInput;
}

export function ChooseType({ onNext, input }: ChooseTypeProps) {
  const [selected, setSelected] = useState<ProgramComponentCreationType>();
  const { currentWorkspace } = useAuth();

  useEffect(() => {
    if (input?.type) {
      setSelected(programComponentTypes.find((o) => o.value === input.type));
    }
  }, [input]);

  const activeComponentTypes = getActiveComponentTypes(
    currentWorkspace.urlSlug,
  );

  const onSelectComponentType = (option: ProgramComponentCreationType) => {
    if (!option.active) return;

    setSelected(option);
    onNext({
      value: {
        type: option.value,
      },
      isSave: false,
    });
  };
  return (
    <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
      <Box fontSize={24} fontWeight={600} my={4}>
        What type of component will this be?
      </Box>
      <div>
        {activeComponentTypes.map((option) => {
          const { id, active } = option;
          return (
            <Box mb={3} key={`option-${option.id}-${option.value}`}>
              <Option
                option={option}
                onClick={() => onSelectComponentType(option)}
                active={active}
                selected={selected?.id === id}
                elevation={2}
              />
            </Box>
          );
        })}
      </div>
    </div>
  );
}
